import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '../../../src/integrations/supabase/client';
import { US_STATES } from '@/lib/constants';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

export const TestimonyForm = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    author_name: '',
    author_email: '',
    title: '',
    content: '',
    is_anonymous: false,
    state: '',
    region_id: ''
  });

  const findRegionByState = async (state: string) => {
    const { data, error } = await supabase
      .from('regions')
      .select('id')
      .contains('states', [state])
      .single();
    
    if (error) {
      console.error('Error finding region:', error);
      return null;
    }
    return data?.id || null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.state) {
      toast({
        title: "Error",
        description: "Please select your state.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    // Minimum 1 second loading for better UX
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 1000));

    try {
      // Find region based on selected state
      const regionId = await findRegionByState(formData.state);
      
      if (!regionId) {
        throw new Error('Could not determine region for selected state');
      }

      const submissionData = {
        author_name: formData.author_name,
        author_email: formData.author_email,
        title: formData.title,
        content: formData.content,
        is_anonymous: formData.is_anonymous,
        region_id: regionId
      };

      const { error } = await supabase
        .from('testimonies')
        .insert([submissionData]);

      if (error) throw error;

      // Send notification to regional coordinators
      await supabase.functions.invoke('send-testimony-notification', {
        body: { ...submissionData, region_id: regionId }
      });

      // Wait for minimum loading time
      await minLoadingTime;

      toast({
        title: "Success",
        description: "Your testimony has been submitted for review!"
      });

      setFormData({
        author_name: '',
        author_email: '',
        title: '',
        content: '',
        is_anonymous: false,
        state: '',
        region_id: ''
      });
    } catch (error) {
      console.error('Error submitting testimony:', error);
      toast({
        title: "Error",
        description: "Failed to submit testimony. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Share Your Testimony</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="author_name">
              Your Name {!formData.is_anonymous && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="author_name"
              value={formData.author_name}
              onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
              required={!formData.is_anonymous}
              disabled={formData.is_anonymous}
              placeholder={formData.is_anonymous ? "Anonymous" : "Enter your name"}
            />
          </div>

          <div>
            <Label htmlFor="author_email">
              Email {!formData.is_anonymous && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="author_email"
              type="email"
              value={formData.author_email}
              onChange={(e) => setFormData({ ...formData, author_email: e.target.value })}
              required={!formData.is_anonymous}
              disabled={formData.is_anonymous}
              placeholder={formData.is_anonymous ? "Anonymous" : "your email"}
            />
          </div>

          <div>
            <Label htmlFor="state">State <span className="text-destructive">*</span></Label>
            <Select 
              value={formData.state} 
              onValueChange={(value) => setFormData({ ...formData, state: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="content">Your Testimony</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Share how God has worked in your life..."
              rows={6}
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_anonymous"
              checked={formData.is_anonymous}
              onCheckedChange={(checked) => 
                setFormData({ ...formData, is_anonymous: checked as boolean })
              }
            />
            <Label htmlFor="is_anonymous">Submit anonymously</Label>
          </div>

          <Button type="submit" disabled={loading} className="flex items-center gap-2">
            {loading ? (
              <>
                <LoadingSpinner size={16} />
                <span>Submitting...</span>
              </>
            ) : (
              'Submit Testimony'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};